import Cookies from "js-cookie";

export const FIRST_ATTR_COOKIE = "dash_first_touch";
export const LAST_ATTR_COOKIE = "dash_last_touch";

export const TRACKED_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
] as const;

type TrackedParam = (typeof TRACKED_PARAMS)[number];

export type AttributionPayload = Partial<Record<TrackedParam, string>> & {
  referrer?: string;
  captured_at: string;
};

export type Attribution = Omit<AttributionPayload, "captured_at">;

// The backend's AttributionInput accepts ONLY these fields. Because the cookies
// live on the shared `.dashtodash.com` parent domain, the WordPress landing
// tracker can write them with extra fields (e.g. `landing_page`, `timestamp`).
// Sending those makes the strict GraphQL input reject the whole mutation with
// BAD_USER_INPUT — on the signup confirm screen that surfaced as a misleading
// "Invalid or expired Code" even when the code was correct (DSH-882). Whitelist
// to the accepted fields right before sending so extras can never break a request.
const ALLOWED_ATTRIBUTION_KEYS = [...TRACKED_PARAMS, "referrer"] as const;

export const sanitizeAttribution = (
  attribution: Record<string, unknown> | undefined
): Attribution | undefined => {
  if (!attribution) return undefined;

  const clean: Record<string, string> = {};
  for (const key of ALLOWED_ATTRIBUTION_KEYS) {
    const value = attribution[key];
    if (typeof value === "string" && value) clean[key] = value;
  }

  return Object.keys(clean).length > 0 ? (clean as Attribution) : undefined;
};

export const readAttributionCookie = (
  cookieName: string
): Attribution | undefined => {
  const raw = Cookies.get(cookieName);
  if (!raw) return undefined;

  try {
    const { captured_at, ...rest } = JSON.parse(raw) as AttributionPayload;
    return rest;
  } catch {
    return undefined;
  }
};

/** First touch ever recorded for this browser — the acquisition channel. */
export const readFirstAttribution = (): Attribution | undefined =>
  sanitizeAttribution(readAttributionCookie(FIRST_ATTR_COOKIE));

/** Most recent touch — what brought the visitor back this time. */
export const readLastAttribution = (): Attribution | undefined =>
  sanitizeAttribution(readAttributionCookie(LAST_ATTR_COOKIE));
